/**
 * 삼성 헬스 브릿지 클라이언트
 *
 * nativeBridge 는 단방향 fire-and-forget 이라 응답을 기다릴 수 없다.
 * 여기서 requestId 를 붙여 보내고 같은 id 의 HEALTH_RESULT / HEALTH_ERROR 만 집어
 * Promise 로 접어준다. 걸음수 화면은 진입하자마자 가용성/권한/오늘/주간을 연달아
 * 물어보기 때문에 id 없이 kind 만으로 맞추면 응답이 섞인다.
 */
import { isNativeApp, onNativeMessage, postToNative } from './nativeBridge';
import MessageTypes from './messageTypes';

let seq = 0;
const nextRequestId = () => `health-${Date.now()}-${++seq}`;

/** 기본 타임아웃. 네이티브가 그 타입을 모르면 응답이 영영 안 오므로 반드시 필요하다. */
const DEFAULT_TIMEOUT_MS = 15000;

/**
 * 권한 요청은 삼성 헬스 동의 팝업을 사용자가 닫아야 응답이 온다.
 * 15초로 끊으면 정상 흐름에서 실패로 보이므로 넉넉히 잡는다.
 */
const PERMISSION_TIMEOUT_MS = 5 * 60 * 1000;

const bridgeError = (code, message) =>
    Object.assign(new Error(message), { code });

function request(type, payload = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
    if (!isNativeApp()) {
        return Promise.reject(
            bridgeError('NOT_NATIVE', '앱에서만 삼성 헬스 데이터를 불러올 수 있습니다.'),
        );
    }

    const requestId = nextRequestId();

    return new Promise((resolve, reject) => {
        let done = false;

        const finish = (fn, arg) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            unsubscribe();
            fn(arg);
        };

        const unsubscribe = onNativeMessage((msg) => {
            if (msg.payload?.requestId !== requestId) return;

            if (msg.type === MessageTypes.HEALTH_RESULT) {
                finish(resolve, msg.payload.data);
            } else if (msg.type === MessageTypes.HEALTH_ERROR) {
                finish(
                    reject,
                    bridgeError(
                        msg.payload.code || 'UNKNOWN_ERROR',
                        msg.payload.error || '삼성 헬스 데이터를 불러오지 못했습니다.',
                    ),
                );
            }
        });

        const timer = setTimeout(
            () => finish(reject, bridgeError('TIMEOUT', '삼성 헬스 응답이 없습니다.')),
            timeoutMs,
        );

        postToNative(type, { ...payload, requestId });
    });
}

/** { available, reason?, shealthInstalled } — reason: OS_VERSION | APP_NOT_INSTALLED | UNSUPPORTED_PLATFORM */
export const fetchAvailability = () =>
    request(MessageTypes.HEALTH_AVAILABILITY);

/** 동의 화면 없이 현재 권한만 확인. { granted } */
export const fetchPermissionStatus = () =>
    request(MessageTypes.HEALTH_PERMISSION_STATUS);

/** 동의 팝업(걸음수·운동·혈압). 사용자가 닫은 뒤의 걸음수 허용 여부. { granted } */
export const requestPermission = () =>
    request(MessageTypes.HEALTH_PERMISSION_REQUEST, {}, PERMISSION_TIMEOUT_MS);

/** 하루 합계. { date, totalSteps } */
export const fetchSteps = (date) =>
    request(MessageTypes.HEALTH_STEPS, { date });

/** 기간 일자별 합계(양끝 포함). [{ date, steps }] */
export const fetchStepsSummary = (startDate, endDate) =>
    request(MessageTypes.HEALTH_STEPS_SUMMARY, { startDate, endDate });

/* ── 운동 기록 (삼성 헬스) ───────────────────────────────────── */

/** 삼성 헬스 사용 가능 여부. { available, reason?, shealthInstalled } */
export const fetchExerciseAvailability = () =>
    request(MessageTypes.HEALTH_EXERCISE_AVAILABILITY);

/** 운동 기록 권한만 확인. 동의 팝업은 안 뜬다. { granted } */
export const fetchExercisePermissionStatus = () =>
    request(MessageTypes.HEALTH_EXERCISE_PERMISSION_STATUS);

/** 운동 기록 동의 팝업. { granted } */
export const requestExercisePermission = () =>
    request(MessageTypes.HEALTH_EXERCISE_PERMISSION_REQUEST, {}, PERMISSION_TIMEOUT_MS);

/**
 * 기간 내 운동 기록(양끝 포함).
 * [{ id, exerciseType, title, startTime, endTime, minutes, calories,
 *    distance, meanHeartRate, autoDetected, dataOrigin }]
 */
export const fetchExerciseSessions = (startDate, endDate) =>
    request(MessageTypes.HEALTH_EXERCISE_SESSIONS, { startDate, endDate });

/* ── 혈압 (삼성 헬스) ──────────────────────────────────────
 * 가용성은 운동과 같은 판정(삼성 헬스 설치 여부)이라 fetchExerciseAvailability 를 같이 쓴다.
 * 권한만 따로다.
 */

/** 혈압 권한만 확인. 동의 팝업은 안 뜬다. { granted } */
export const fetchBpPermissionStatus = () =>
    request(MessageTypes.HEALTH_BP_PERMISSION_STATUS);

/** 혈압 동의 팝업. { granted } */
export const requestBpPermission = () =>
    request(MessageTypes.HEALTH_BP_PERMISSION_REQUEST, {}, PERMISSION_TIMEOUT_MS);

/**
 * 기간 내 혈압 기록(양끝 포함). 하루 여러 건이 정상이다.
 * [{ uid, systolic, diastolic, time, pulseRate, medicationTaken, appId }]
 */
export const fetchBpReadings = (startDate, endDate) =>
    request(MessageTypes.HEALTH_BP_READINGS, { startDate, endDate });

const SAMSUNG_HEALTH_PACKAGE = 'com.sec.android.app.shealth';

/** 삼성 헬스 스토어 페이지 열기. 단방향 메시지 */
export const openSamsungHealthStore = () => {
    const storeUrl = `https://play.google.com/store/apps/details?id=${SAMSUNG_HEALTH_PACKAGE}`;
    if (isNativeApp()) {
        postToNative(MessageTypes.LAUNCH_APP, {
            android: { scheme: `market://details?id=${SAMSUNG_HEALTH_PACKAGE}`, storeUrl },
        });
    } else {
        window.open(storeUrl, '_blank');
    }
};
